import React, { useState, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import { useLead } from "../context/LeadContext";

const fmt = (n) =>
  "₹" + Math.round(n).toLocaleString("en-IN");

export const EMICalculator = () => {
  const [amount, setAmount] = useState(1000000);
  const [rate, setRate] = useState(11);
  const [tenure, setTenure] = useState(60);
  const { openLead } = useLead();

  const { emi, totalInterest, totalPayable } = useMemo(() => {
    const p = amount;
    const r = rate / 12 / 100;
    const n = tenure;
    const e = r === 0 ? p / n : (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = e * n;
    return { emi: e, totalInterest: total - p, totalPayable: total };
  }, [amount, rate, tenure]);

  const data = [
    { name: "Principal", value: amount },
    { name: "Interest", value: totalInterest },
  ];
  const COLORS = ["#D4AF37", "#10B981"];

  return (
    <div className="card-dark rounded-2xl p-6 sm:p-8" data-testid="emi-calculator">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-7">
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-slate-300">Loan Amount</label>
              <span className="font-mono-fin text-gold font-bold text-lg" data-testid="emi-amount-value">{fmt(amount)}</span>
            </div>
            <Slider
              data-testid="emi-amount-slider"
              value={[amount]} min={50000} max={50000000} step={50000}
              onValueChange={(v) => setAmount(v[0])}
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1.5">
              <span>₹50K</span><span>₹5 Cr</span>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-slate-300">Interest Rate (p.a.)</label>
              <span className="font-mono-fin text-gold font-bold text-lg" data-testid="emi-rate-value">{rate}%</span>
            </div>
            <Slider
              data-testid="emi-rate-slider"
              value={[rate]} min={7} max={26} step={0.25}
              onValueChange={(v) => setRate(v[0])}
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1.5">
              <span>7%</span><span>26%</span>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-slate-300">Tenure (months)</label>
              <span className="font-mono-fin text-gold font-bold text-lg" data-testid="emi-tenure-value">{tenure} mo</span>
            </div>
            <Slider
              data-testid="emi-tenure-slider"
              value={[tenure]} min={6} max={360} step={6}
              onValueChange={(v) => setTenure(v[0])}
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1.5">
              <span>6 mo</span><span>30 yr</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center">
          <div className="relative w-full h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} innerRadius={58} outerRadius={82} paddingAngle={3} dataKey="value" stroke="none">
                  {data.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Monthly EMI</span>
              <span className="font-mono-fin text-2xl font-extrabold gold-text" data-testid="emi-monthly-amount-display">
                {fmt(emi)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 w-full mt-6">
            <div className="rounded-xl bg-obsidian-900/60 border border-white/10 p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <span className="w-2.5 h-2.5 rounded-full bg-gold inline-block" /> Principal
              </div>
              <div className="font-mono-fin font-bold text-white mt-1">{fmt(amount)}</div>
            </div>
            <div className="rounded-xl bg-obsidian-900/60 border border-white/10 p-3 text-center">
              <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <span className="w-2.5 h-2.5 rounded-full bg-approval inline-block" /> Interest
              </div>
              <div className="font-mono-fin font-bold text-white mt-1">{fmt(totalInterest)}</div>
            </div>
          </div>
          <div className="w-full mt-3 rounded-xl bg-gold/10 border border-gold/25 p-3 text-center">
            <span className="text-xs text-slate-300">Total Payable</span>
            <div className="font-mono-fin font-bold text-gold text-lg">{fmt(totalPayable)}</div>
          </div>

          <Button
            onClick={() => openLead({ source: "emi-calculator", amount: String(amount) })}
            data-testid="emi-apply-button"
            className="w-full mt-4 gold-gradient text-obsidian-900 font-bold h-11 rounded-full hover:opacity-90"
          >
            Apply for This Loan
          </Button>
        </div>
      </div>
    </div>
  );
};
